import {graphql} from 'gatsby'
import React from 'react'

import Button from '@/components/button'
import PrismicLink from '@/components/prismic-link'
import {Text} from '@/components/text'
import type {
    RatingsPlusHeadlineSliceFragment,
    PartnerTemplateRatingsPlusHeadlineFragment,
    RatingsPlusHeadlineFragment,
} from '@/graphql-types'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'

type Props = {
    slice:
        | RatingsPlusHeadlineSliceFragment
        | PartnerTemplateRatingsPlusHeadlineFragment
        | RatingsPlusHeadlineFragment
    dataTestId?: string
}

export const RatingsPlusHeadlineSlice = React.forwardRef<HTMLElement, Props>(
    ({slice, dataTestId}, ref) => {
        const title =
            slice.primary.predefined_ratings.document?.data.title ?? slice.primary.ratings_title
        const leadText =
            slice.primary.predefined_ratings.document?.data.lead_text ??
            slice.primary.ratings_lead_text
        const ctaText =
            slice.primary.predefined_ratings.document?.data.cta_text ??
            slice.primary.ratings_cta_text
        const ctaLink =
            slice.primary.predefined_ratings.document?.data.cta_link ?? slice.primary.cta_link
        const itemsObject = slice.primary.predefined_ratings.document?.data.ratings ?? slice.items
        const hasStarsRating = itemsObject.some((item) => !!item.rating.document.data.rating_number)

        return (
            <TitleWithIconGridSection
                ref={ref}
                titleVariant={!leadText ? 'heading-large' : 'heading-base'}
                highlightColor="pink"
                title={title.richText}
                items={itemsObject.map((item) => ({
                    icon: item.rating.document.data.logo,
                    stars: item.rating.document.data.rating_number,
                    link: item.rating.document.data.link,
                }))}
                {...(!hasStarsRating && {iconHeight: 187, iconWidth: 144})}
                dataTestId={dataTestId}
            >
                <>
                    {leadText && <Text>{leadText}</Text>}

                    {ctaText && (
                        <Button as={PrismicLink} $variant="outline" {...ctaLink}>
                            {ctaText}
                        </Button>
                    )}
                </>
            </TitleWithIconGridSection>
        )
    },
)

export const ratingsPlusHeadlineFragment = graphql`
    fragment RatingsPlusHeadlineSlice on PrismicSlicePageDataBodyRatingsPlusHeadline {
        __typename
        primary {
            ratings_title {
                richText
            }
            ratings_lead_text
            ratings_cta_text
            cta_link {
                ...PrismicLink
            }
            predefined_ratings {
                document {
                    ... on PrismicSocialProofRatingsSection {
                        data {
                            title {
                                richText
                            }
                            lead_text
                            cta_text
                            cta_link {
                                ...PrismicLink
                            }
                            ratings {
                                rating {
                                    document {
                                        ... on PrismicRating {
                                            data {
                                                logo {
                                                    alt
                                                    copyright
                                                    url
                                                    dimensions {
                                                        height
                                                        width
                                                    }
                                                }
                                                rating_number
                                                link
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        items {
            rating {
                document {
                    ... on PrismicRating {
                        data {
                            logo {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                            }
                            rating_number
                            link
                        }
                    }
                }
            }
        }
    }
`
